import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, FileDown, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, formatPrice, STATUS_LABELS, BACKEND_URL, getToken } from "@/lib/api";

const ALL_STATUS = Object.keys(STATUS_LABELS);

export default function AdminRequestDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/requests/${id}`).then((r) => { setDoc(r.data); setEdit({ status: r.data.status, final_price: r.data.final_price ?? "", payment_status: r.data.payment_status, internal_note: r.data.internal_note ?? "" }); }).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...edit };
      if (payload.final_price === "") payload.final_price = null;
      else payload.final_price = Number(payload.final_price);
      await api.patch(`/admin/requests/${id}`, payload);
      toast.success("Mise à jour");
      load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  const pdfUrl = (type) => `${BACKEND_URL}/api/admin/requests/${id}/pdf/${type}`;

  if (!doc) return <div className="text-zinc-400">Chargement…</div>;

  return (
    <div>
      <Link to="/admin/demandes" className="inline-flex items-center gap-2 text-zinc-400 hover:text-brand text-sm mb-4"><ArrowLeft size={14} /> Retour</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-heading text-3xl lg:text-4xl font-black uppercase tracking-tight">Demande {doc.request_number}</div>
          <div className="text-zinc-400 text-sm mt-1">Créée le {(doc.created_at || "").slice(0, 16).replace("T", " ")}</div>
        </div>
        <div className="flex gap-2">
          <a href={`tel:${doc.customer_phone}`} className="bg-brand text-white px-4 py-2 font-bold uppercase text-sm inline-flex items-center gap-2"><Phone size={14} /> Appeler</a>
          <a href={`https://wa.me/${doc.customer_phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="bg-whats text-white px-4 py-2 font-bold uppercase text-sm inline-flex items-center gap-2"><MessageCircle size={14} /> WhatsApp</a>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <Section title="Client" cols={2}>
          <Row label="Nom" value={doc.customer_name} />
          <Row label="Téléphone" value={doc.customer_phone} />
          <Row label="Email" value={doc.customer_email || "—"} />
          <Row label="Préférence" value={doc.preferred_contact} />
        </Section>
        <Section title="Véhicule" cols={2}>
          <Row label="Type" value={doc.vehicle_type || "—"} />
          <Row label="Marque" value={doc.vehicle_brand || "—"} />
          <Row label="Modèle" value={doc.vehicle_model || "—"} />
          <Row label="Plaque" value={doc.vehicle_plate || "—"} />
        </Section>
        <Section title="Intervention" cols={1}>
          <Row label="Service" value={doc.service_type} />
          <Row label="Urgence" value={doc.urgency} />
          <Row label="Adresse de départ" value={doc.pickup_address} />
          <Row label="Destination" value={doc.dropoff_address || "—"} />
        </Section>
      </div>

      <div className="mt-4 bg-ink-900 border border-ink-700 p-5">
        <div className="font-heading text-xl font-bold uppercase tracking-tight mb-4">Pilotage</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <div className="text-xs uppercase text-zinc-500 mb-1">Statut</div>
            <select className="w-full h-10 px-3 bg-ink-800 border border-ink-700 focus:border-brand text-white outline-none" value={edit.status} onChange={(e) => setEdit((s) => ({ ...s, status: e.target.value }))} data-testid="admin-status-select">
              {ALL_STATUS.map((k) => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
            </select>
          </div>
          <div>
            <div className="text-xs uppercase text-zinc-500 mb-1">Prix final (€)</div>
            <input type="number" step="0.01" className="w-full h-10 px-3 bg-ink-800 border border-ink-700 focus:border-brand text-white outline-none" value={edit.final_price} onChange={(e) => setEdit((s) => ({ ...s, final_price: e.target.value }))} data-testid="admin-final-price" />
          </div>
          <div>
            <div className="text-xs uppercase text-zinc-500 mb-1">Paiement</div>
            <select className="w-full h-10 px-3 bg-ink-800 border border-ink-700 focus:border-brand text-white outline-none" value={edit.payment_status} onChange={(e) => setEdit((s) => ({ ...s, payment_status: e.target.value }))}>
              <option value="non_paye">Non payé</option>
              <option value="acompte">Acompte payé</option>
              <option value="paye">Payé</option>
              <option value="en_attente">En attente</option>
              <option value="rembourse">Remboursé</option>
            </select>
          </div>
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="text-xs uppercase text-zinc-500 mb-1">Note interne</div>
            <input className="w-full h-10 px-3 bg-ink-800 border border-ink-700 focus:border-brand text-white outline-none" value={edit.internal_note || ""} onChange={(e) => setEdit((s) => ({ ...s, internal_note: e.target.value }))} placeholder="Notes équipe" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button disabled={saving} onClick={save} className="bg-brand text-white font-heading font-black uppercase px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-save-btn"><Save size={16} /> Enregistrer</button>
          <a href={pdfUrl("quote")} target="_blank" rel="noreferrer" className="border border-ink-600 text-white hover:border-brand px-5 py-2.5 inline-flex items-center gap-2 text-sm uppercase font-bold tracking-wider"><FileDown size={14} /> Devis PDF</a>
          <a href={pdfUrl("invoice")} target="_blank" rel="noreferrer" className="border border-ink-600 text-white hover:border-brand px-5 py-2.5 inline-flex items-center gap-2 text-sm uppercase font-bold tracking-wider"><FileDown size={14} /> Facture PDF</a>
          <a href={pdfUrl("intervention")} target="_blank" rel="noreferrer" className="border border-ink-600 text-white hover:border-brand px-5 py-2.5 inline-flex items-center gap-2 text-sm uppercase font-bold tracking-wider"><FileDown size={14} /> Bon d'intervention</a>
        </div>
        <div className="text-zinc-500 text-xs mt-2">Astuce : un PDF requiert d'être connecté admin (authentification incluse via votre session).</div>
      </div>

      <div className="mt-4 bg-ink-900 border border-ink-700 p-5">
        <div className="font-heading text-xl font-bold uppercase tracking-tight mb-3">Description</div>
        <p className="text-zinc-300">{doc.description || "—"}</p>
      </div>

      <div className="mt-4 bg-ink-900 border border-ink-700 p-5">
        <div className="font-heading text-xl font-bold uppercase tracking-tight mb-3">Historique</div>
        <ul className="space-y-2">
          {(doc.history || []).map((h, i) => (
            <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
              <span className="w-2 h-2 rounded-full bg-brand" />
              <span className="font-bold">{STATUS_LABELS[h.new_status] || h.new_status}</span>
              {h.note && <span className="text-zinc-500">— {h.note}</span>}
              <span className="text-zinc-600 text-xs ml-auto">{(h.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </li>
          ))}
        </ul>
      </div>

      {doc.photos?.length > 0 && (
        <div className="mt-4 bg-ink-900 border border-ink-700 p-5">
          <div className="font-heading text-xl font-bold uppercase tracking-tight mb-3">Photos</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {doc.photos.map((p, i) => <img key={i} src={p} alt="" className="w-full h-32 object-cover" />)}
          </div>
        </div>
      )}

      <div className="text-zinc-600 text-xs mt-3">Token: {getToken() ? "OK" : "—"} | Backend: {BACKEND_URL}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-ink-900 border border-ink-700 p-5">
      <div className="font-heading text-lg font-bold uppercase tracking-tight mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
